import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import CommonButton from "../../components/widgets/common_button";
import CommonPagination from "../../components/widgets/common_pagination";
import UserService from "../../service/user.service";
import Toaster from "../../components/common/toaster";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  User,
  Briefcase,
  AlertTriangle,
  Building,
  MapPin,
} from "lucide-react";

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [statusChanging, setStatusChanging] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [userJobs, setUserJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsSize, setJobsSize] = useState(6);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalJobsPages, setTotalJobsPages] = useState(0);

  // Handle back navigation
  const handleBackNavigation = () => {
    const navigationState = location.state;

    if (navigationState?.from) {
      navigate(navigationState.from);
    } else {
      // Fallback to simple navigation
      navigate("/users");
    }
  };

  const capitalize = (text) => {
    if (typeof text !== "string" || text.length === 0) return "-";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const imagePlaceholder =
    "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

  // Fetch user jobs for employers
  const fetchUserJobs = async (userId, page = 1, limit = 6) => {
    setJobsLoading(true);
    try {
      const response = await UserService.getUserJobs({
        userId,
        page,
        limit,
      });

      if (response?.data?.data) {
        setUserJobs(response.data.data);
        setTotalJobs(response.data.total || response.data.data.length);
        setTotalJobsPages(
          Math.ceil((response.data.total || response.data.data.length) / limit)
        );
      } else {
        setUserJobs([]);
        setTotalJobs(0);
        setTotalJobsPages(0);
      }
    } catch (error) {
      console.error("Failed to fetch user jobs:", error);
      Toaster("error", "Failed to fetch user jobs");
      setUserJobs([]);
      setTotalJobs(0);
      setTotalJobsPages(0);
    } finally {
      setJobsLoading(false);
    }
  };

  // Get user data from navigation state or redirect if not available
  useEffect(() => {
    const userFromState = location.state?.user;
    const filterFromState = location.state?.currentFilter;

    if (userFromState) {
      setUser(userFromState);
      setCurrentFilter(filterFromState);

      // If user is an employer, fetch their jobs
      if (
        filterFromState === "employers" ||
        userFromState?.role === "employer"
      ) {
        fetchUserJobs(userFromState._id, jobsPage, jobsSize);
      }
    } else {
      // If no user data in state, redirect back to users list
      Toaster("error", "User data not found. Redirecting to users list.");
      navigate("/users");
    }
  }, [location.state, navigate, jobsPage, jobsSize]);

  const handleStatusChange = async (value) => {
    setStatusChanging(true);
    try {
      await UserService.toggleUserActive(userId);

      // Update local state
      setUser((prev) => ({
        ...prev,
        isActive: value === "un-block",
      }));

      Toaster(
        "success",
        `User ${value === "un-block" ? "unblocked" : "blocked"} successfully`
      );
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to update user status"
      );
    } finally {
      setStatusChanging(false);
      setConfirmDialogOpen(false);
    }
  };

  const formatText = (text) => {
    if (!text) return "-";
    return text
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleJobsPageChange = (newPage) => {
    setJobsPage(newPage);
    if (user) {
      fetchUserJobs(user._id, newPage, jobsSize);
    }
  };

  const handleJobsSizeChange = (newSize) => {
    setJobsSize(newSize);
    setJobsPage(1);
    if (user) {
      fetchUserJobs(user._id, 1, newSize);
    }
  };

  const getJobCategoryIcon = (category) => {
    const categoryIcons = {
      entry_level_jobs: require("../../assets/icon/Entry.png"),
      deliveries_distribution: require("../../assets/icon/Distribution.png"),
      maintenance_cleaning: require("../../assets/icon/Cleaning.png"),
      warehouse_packing: require("../../assets/icon/Packing.png"),
      food_service: require("../../assets/icon/Food.png"),
      customer_service: require("../../assets/icon/Customer.png"),
      sales: require("../../assets/icon/Sales.png"),
      drivers_couriers: require("../../assets/icon/Drivers.png"),
      physical_labor: require("../../assets/icon/Physical.png"),
      technicians: require("../../assets/icon/Technician.png"),
      temporary_jobs: require("../../assets/icon/Temporary.png"),
      service_providers: require("../../assets/icon/Service.png"),
      // Alternative mappings
      "entry-level jobs": require("../../assets/icon/Entry.png"),
      "deliveries & distribution": require("../../assets/icon/Distribution.png"),
      "maintenance & cleaning": require("../../assets/icon/Cleaning.png"),
      "warehouse & packing": require("../../assets/icon/Packing.png"),
      "food service": require("../../assets/icon/Food.png"),
      "customer service": require("../../assets/icon/Customer.png"),
      "drivers & couriers": require("../../assets/icon/Drivers.png"),
      "physical labor": require("../../assets/icon/Physical.png"),
      "temporary jobs": require("../../assets/icon/Temporary.png"),
      "service providers": require("../../assets/icon/Service.png"),
    };
    return categoryIcons[category] || require("../../assets/icon/Entry.png");
  };

  const getJobCategoryName = (category) => {
    const categoryNames = {
      entry_level_jobs: "Entry-Level Jobs",
      deliveries_and_distribution: "Deliveries and Distribution",
      maintenance_and_cleaning: "Maintenance and Cleaning",
      warehouse_and_packing: "Warehouse and Packing",
      food_service: "Food Service",
      customer_service: "Customer Service",
      sales: "Sales",
      drivers_and_couriers: "Drivers and Couriers",
      physical_labor: "Physical Labor",
      technicians: "Technicians",
      temporary_jobs: "Temporary Jobs",
      service_providers: "Service Providers",
    };
    return categoryNames[category] || formatText(category);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            User Not Found
          </h2>
          <p className="text-gray-500 mb-6">
            The requested user could not be found.
          </p>
          <CommonButton
            variant="outline"
            onClick={handleBackNavigation}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </CommonButton>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-screen bg-gray-50">
      <div className="max-w-7xl px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header Section */}
        <div className="flex items-center gap-4 justify-between w-full mb-4 sm:mb-6">
          <Button
            variant="outline"
            size="lg"
            onClick={handleBackNavigation}
            className="flex items-center gap-2 h-8 sm:h-10 px-3 sm:px-4"
          >
            <ArrowLeft className="h-4 w-4 sm:h-6 sm:w-6" />
            <span className="text-sm sm:text-lg">Back</span>
          </Button>
        </div>

        {/* Main Card */}
        <Card className="p-4 sm:p-6 lg:p-8">
          {/* Profile Section */}
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4 lg:gap-6 mb-6 sm:mb-8">
            {/* Profile Image */}
            <div className="flex-shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-4 border-gray-200">
                <img
                  src={user.profilePic || imagePlaceholder}
                  alt="User Profile"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {capitalize(user?.firstName)} {capitalize(user?.lastName)}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mb-2 break-all">
                {user.email}
              </p>
              <div className="flex items-center justify-center lg:justify-start gap-2">
                <Building className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm text-gray-600">
                  {formatText(user?.role)}
                </span>
              </div>
            </div>

            {/* Status Section */}
            <div className="flex-shrink-0 w-full lg:w-auto">
              <div className="text-center lg:text-right">
                <p className="text-sm font-medium text-gray-700 mb-2">Status</p>
                <div className="flex justify-center lg:justify-end">
                  {statusChanging ? (
                    <div className="w-32 h-10 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                    </div>
                  ) : (
                    <Select
                      onValueChange={(value) => {
                        if (value !== (user.isActive ? "un-block" : "block")) {
                          setConfirmDialogOpen(true);
                        }
                      }}
                      defaultValue={user.isActive ? "un-block" : "block"}
                    >
                      <SelectTrigger
                        className={`w-32 font-medium ${
                          user.isActive
                            ? "!border-green-500 text-green-600 bg-green-50"
                            : "!border-red-500 text-red-600 bg-red-50"
                        }`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="block">Block</SelectItem>
                        <SelectItem value="un-block">Unblock</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 mb-8"></div>

          {/* Conditional Section - Jobs for Employers, Categories for Job Seekers */}
          <div>
            {currentFilter === "employers" || user?.role === "employer" ? (
              // Jobs Section for Employers
              <>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
                  Posted Jobs
                </h2>

                {jobsLoading ? (
                  <div className="text-center py-6 sm:py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p className="text-gray-500 text-sm sm:text-base">
                      Loading jobs...
                    </p>
                  </div>
                ) : userJobs && userJobs.length > 0 ? (
                  <div className="space-y-6">
                    <div className="max-h-[400px] sm:max-h-[500px] lg:max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {userJobs.map((item, index) => (
                          <Card
                            key={item._id || index}
                            className="overflow-hidden hover:shadow-lg transition-shadow duration-200 h-[450px] flex flex-col"
                          >
                            {/* Job Image */}
                            <div className="h-90 w-full overflow-hidden">
                              <img
                                src={item.jobImg || imagePlaceholder}
                                alt={capitalize(item?.title)}
                                className="h-150 w-full object-cover"
                              />
                            </div>

                            {/* Card Content */}
                            <div className="p-4 flex flex-col h-full">
                              {/* User Profile Section */}
                              <div className="flex items-center gap-3 mb-3">
                                <div className="h-10 w-10 overflow-hidden rounded-full flex-shrink-0">
                                  <img
                                    src={user?.profilePic || imagePlaceholder}
                                    alt="Profile"
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm text-gray-900 truncate">
                                    {capitalize(user?.firstName)}{" "}
                                    {capitalize(user?.lastName)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Job Creator
                                  </p>
                                </div>
                              </div>

                              {/* Job Title */}
                              <div className="mb-2">
                                <div className="flex items-center gap-2">
                                  <label className="font-medium text-lg">
                                    Title:
                                  </label>
                                  <h3 className="font-bold text-base text-gray-700 line-clamp-2">
                                    {capitalize(item?.title) || "Untitled Job"}
                                  </h3>
                                </div>
                              </div>

                              {/* Job Description */}
                              <div className="flex-1 mb-3">
                                <p className="text-sm text-gray-600 line-clamp-2">
                                  {capitalize(item?.description) ||
                                    "No description available"}
                                </p>
                              </div>

                              {/* Location */}
                              <div className="mb-4">
                                <div className="flex items-center gap-2 bg-purple-50 rounded-lg p-2">
                                  <MapPin className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                  <p className="text-sm text-gray-700 line-clamp-1">
                                    {capitalize(item?.location)}
                                  </p>
                                </div>
                              </div>

                              {/* Actions - Fixed at bottom */}
                              {/* <div className="flex justify-end mt-auto">
                              <div className="flex items-center gap-2">
                                <CommonButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/jobs/${item._id}`, {
                                      state: {
                                        job: item,
                                        from: `/users/${user._id}`,
                                        currentFilter: currentFilter,
                                      },
                                    })
                                  }
                                  className="flex items-center p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Eye className="h-4 w-4" />
                                </CommonButton>
                              </div>
                            </div> */}
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Pagination */}
                    {totalJobs > jobsSize && (
                      <div className="flex justify-center">
                        <CommonPagination
                          currentPage={jobsPage}
                          totalPages={totalJobsPages}
                          onPageChange={handleJobsPageChange}
                          pageSize={jobsSize}
                          onPageSizeChange={handleJobsSizeChange}
                          pageSizeOptions={["6", "12", "18", "24", "30"]}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <Briefcase className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base">
                      No jobs posted yet
                    </p>
                  </div>
                )}
              </>
            ) : (
              // Categories Section for Job Seekers
              <>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
                  Job Categories
                </h2>

                {user.jobCategories && user.jobCategories.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {user.jobCategories.map((category, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow duration-200"
                      >
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <img
                            src={getJobCategoryIcon(category)}
                            alt={getJobCategoryName(category)}
                            className="w-6 h-6 object-contain"
                            style={{
                              imageRendering: "pixelated",
                            }}
                          />
                        </div>
                        <p className="text-sm font-medium text-gray-900 break-words">
                          {getJobCategoryName(category)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <Briefcase className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm sm:text-base">
                      No categories assigned
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to {user.isActive ? "block" : "unblock"}{" "}
              this user?
              {user.isActive ? (
                <span className="block mt-2 text-red-600">
                  This will prevent the user from accessing the platform.
                </span>
              ) : (
                <span className="block mt-2 text-green-600">
                  This will restore the user's access to the platform.
                </span>
              )}
            </p>
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <CommonButton
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant={user.isActive ? "destructive" : "default"}
              onClick={() =>
                handleStatusChange(user.isActive ? "block" : "un-block")
              }
              disabled={statusChanging}
              className="w-full sm:w-auto"
            >
              {statusChanging ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                `${user.isActive ? "Block" : "Unblock"} User`
              )}
            </CommonButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDetails;
